import assertNever from "assert-never";
import * as React from "react";
import { Fragment, ReactElement } from "react";
import { getAWSPollyMarks } from "../../../API";
import {
  FullDescriptionData,
  FullDescriptionSpeechMark,
  isPlayingDescriptionType,
  TrainingCenterBundleItemFullDescription,
} from "../../../system/AppState/AppStateTrainingCenter";
import { arrayAt } from "../../../utils/arrayUtils";
import { List } from "../../../utils/List";
import { TrainingCenterLanguagePreference } from "../TrainingCenterLanguageLookup";
import TrainingCenterBundleItemFullDescriptionList from "./TrainingCenterBundleItemFullDescriptionList";

export interface TrainingCenterBundleItemFullDescriptionActivityProps {
  languagePreference: TrainingCenterLanguagePreference;
  fullDescriptions: List<TrainingCenterBundleItemFullDescription>;
}

export interface CurrentSpeechMark {
  word: string;
  mark: FullDescriptionSpeechMark | null;
}
const PUNCTUATION_MARKS_REGEX = /[.,/#!$%^&*;:{}=\-_`~()]/;
const TrainingCenterBundleItemFullDescriptionActivity = React.memo(
  function TrainingCenterTestQuestionsActivity(
    props: TrainingCenterBundleItemFullDescriptionActivityProps
  ): ReactElement {
    const [descriptions, setDescriptions] = React.useState(props.fullDescriptions);
    const speechMarks = React.useRef<Map<string, CurrentSpeechMark[]>>(
      new Map<string, CurrentSpeechMark[]>()
    );
    const [currentAudioTime, setCurrentAudioTime] = React.useState<number>(0);
    const currentAudioRef = React.useRef<HTMLAudioElement | null>(null);
    const audioPlayingInterval = React.useRef<NodeJS.Timeout>();

    React.useEffect(() => {
      let isMounted = true;
      for (const descriptionRow of props.fullDescriptions) {
        for (const rowLang of descriptionRow.fullDescriptionData) {
          if (rowLang.language === props.languagePreference.preferredLanguage) {
            const titleSpeechMarksFileUrl = rowLang.data.speechFiles.titleSpeechMarksFileUrl;
            const title = rowLang.data.title;
            if (title !== undefined && titleSpeechMarksFileUrl !== undefined) {
              getAWSPollyMarks(titleSpeechMarksFileUrl)
                .then((speechMark) => {
                  if (isMounted) {
                    const formattedSpeechMarks = formatSpeechMarks(title, speechMark);
                    speechMarks.current.set(titleSpeechMarksFileUrl, formattedSpeechMarks);
                  }
                })
                .catch((err) => console.log(err));
            }
            const subtitleSpeechMarksFileUrl = rowLang.data.speechFiles.subtitleSpeechMarksFileUrl;
            const subtitle = rowLang.data.subtitle;
            if (subtitle !== undefined && subtitleSpeechMarksFileUrl !== undefined) {
              getAWSPollyMarks(subtitleSpeechMarksFileUrl)
                .then((speechMark) => {
                  if (isMounted) {
                    const formattedSpeechMarks = formatSpeechMarks(subtitle, speechMark);
                    speechMarks.current.set(subtitleSpeechMarksFileUrl, formattedSpeechMarks);
                  }
                })
                .catch((err) => console.log(err));
            }
            const textSpeechMarksFileUrl = rowLang.data.speechFiles.textSpeechMarksFileUrl;
            const text = rowLang.data.text;
            if (text !== undefined && textSpeechMarksFileUrl !== undefined) {
              getAWSPollyMarks(textSpeechMarksFileUrl)
                .then((speechMark) => {
                  if (isMounted) {
                    const formattedSpeechMarks = formatSpeechMarks(text, speechMark);
                    speechMarks.current.set(textSpeechMarksFileUrl, formattedSpeechMarks);
                  }
                })
                .catch((err) => console.log(err));
            }
          }
        }
      }
      return () => {
        isMounted = false;
      };
    }, [props.fullDescriptions, props.languagePreference.preferredLanguage, speechMarks]);

    const formatSpeechMarks = (
      sentence: string | JSX.Element[],
      unformattedSpeechMarks: FullDescriptionSpeechMark[]
    ) => {
      if (typeof sentence !== "string") {
        return [];
      }
      const marks = unformattedSpeechMarks.map((speechMark, index) => {
        let toValue = speechMark.time + 100;

        // index + 1 can result in undefined -> tho ts can't tell..
        const next = arrayAt(unformattedSpeechMarks, index + 1);
        // tslint:disable-next-line: strict-type-predicates
        if (next !== undefined) {
          toValue = next.time - 1;
        }

        return {
          ...speechMark,
          index: index,
          to: toValue,
        };
      });
      const result = [];
      let index = 0;
      // Sentences can contain "\n"s for new lines..
      for (const word of sentence.replace(/\n/g, " ").split(" ")) {
        // Strip of only punctations "words"
        if (word.replace(PUNCTUATION_MARKS_REGEX, "") === "") {
          result.push({ word: word, mark: null });
          continue;
        }

        result.push({ word: word, mark: marks[index] });
        index++;
      }
      return result;
    };

    const nextPlayingType = React.useMemo(
      () => (descriptionItem: TrainingCenterBundleItemFullDescription) => {
        let response: isPlayingDescriptionType = false;
        const descObj = descriptionItem.fullDescriptionData.find(
          (descData) => descData.language === props.languagePreference.preferredLanguage
        );
        const descriptionData = descObj !== undefined ? descObj.data : undefined;
        if (descriptionData === undefined) {
          return response;
        }
        if (descriptionData.title !== undefined && descriptionData.title !== "") {
          response = "title";
        } else if (descriptionData.subtitle !== undefined && descriptionData.subtitle !== "") {
          response = "subtitle";
        } else if (descriptionData.text !== undefined && descriptionData.text !== "") {
          response = "text";
        }
        return response;
      },
      [props.languagePreference.preferredLanguage]
    );

    const getNextDescriptionItem = React.useMemo(
      () =>
        (
          descItem: TrainingCenterBundleItemFullDescription
        ): TrainingCenterBundleItemFullDescription | undefined => {
          const asArray = descriptions.toArray();
          const currentIndex = asArray.findIndex((item) => descItem === item);
          const nextItem = asArray[currentIndex + 1];
          return nextItem;
        },
      [descriptions]
    );

    const isPlaying: isPlayingDescriptionType = React.useMemo(() => {
      const found = descriptions.find((description) => description.isPlaying !== false);
      if (found === undefined) {
        return false;
      }

      return found.isPlaying;
    }, [descriptions]);

    const updateCurrentTime = React.useCallback(() => {
      if (currentAudioRef.current !== null) {
        setCurrentAudioTime(currentAudioRef.current.currentTime * 1000);
      }
    }, []);

    const playPauseAudio = React.useCallback(
      (play: boolean) => {
        if (play) {
          audioPlayingInterval.current = setInterval(() => updateCurrentTime(), 100);
        } else {
          setCurrentAudioTime(0);
          if (audioPlayingInterval.current !== undefined) {
            clearInterval(audioPlayingInterval.current);
          }
        }
      },
      [updateCurrentTime]
    );

    const handlePlayPauseAudioClick = React.useCallback(() => {
      const playingDescriptionItem = descriptions.find((descItem) => descItem.isPlaying !== false);
      if (playingDescriptionItem !== undefined) {
        // Stop playing the current playing item
        playingDescriptionItem.isPlaying = false;
        playPauseAudio(false);
        setDescriptions(
          descriptions.map((descItem) => {
            if (descItem.itemId === playingDescriptionItem.itemId) {
              return playingDescriptionItem;
            }
            return descItem;
          })
        );
      } else {
        const firstDescriptionItem = descriptions.first();
        if (firstDescriptionItem !== undefined) {
          // Play description by the first available type
          firstDescriptionItem.isPlaying = nextPlayingType(firstDescriptionItem);
          playPauseAudio(true);
          setDescriptions(
            descriptions.map((descItem) => {
              if (descItem.itemId === firstDescriptionItem.itemId) {
                return firstDescriptionItem;
              }
              return descItem;
            })
          );
        }
      }
    }, [descriptions, nextPlayingType, playPauseAudio]);

    const handleAudioPlayEnded = React.useCallback(
      (
        currentPlayingItem: TrainingCenterBundleItemFullDescription,
        currentDecsription: FullDescriptionData
      ) => {
        playPauseAudio(false);
        let playingItem = currentPlayingItem;
        switch (playingItem.isPlaying) {
          case "title":
            if (currentDecsription.subtitle !== undefined) {
              playingItem.isPlaying = "subtitle";
            } else if (currentDecsription.text !== undefined) {
              playingItem.isPlaying = "text";
            } else {
              playingItem.isPlaying = false;
              const nextItem = getNextDescriptionItem(playingItem);
              if (nextItem !== undefined) {
                nextItem.isPlaying = nextPlayingType(nextItem);
                playingItem = nextItem;
              }
            }
            break;
          case "subtitle":
            if (currentDecsription.text !== undefined) {
              playingItem.isPlaying = "text";
            } else {
              playingItem.isPlaying = false;
              const nextItem = getNextDescriptionItem(playingItem);
              if (nextItem !== undefined) {
                nextItem.isPlaying = nextPlayingType(nextItem);
                playingItem = nextItem;
              }
            }
            break;
          case "text": {
            playingItem.isPlaying = false;
            const nextItem = getNextDescriptionItem(playingItem);
            if (nextItem !== undefined) {
              nextItem.isPlaying = nextPlayingType(nextItem);
              playingItem = nextItem;
            }
            break;
          }
          case false:
            // Do nothing
            break;
          default:
            assertNever(playingItem.isPlaying);
        }
        const updated = descriptions.map((item) => {
          let isItemPlaying = item.isPlaying;
          if (item.itemId === playingItem.itemId) {
            isItemPlaying = playingItem.isPlaying;
          }
          return { ...item, isPlaying: isItemPlaying };
        });
        setDescriptions(updated);
        setCurrentAudioTime(0);
        if (playingItem.isPlaying !== false) {
          playPauseAudio(true);
        }
      },
      [descriptions, getNextDescriptionItem, nextPlayingType, playPauseAudio]
    );

    const getText = React.useCallback(
      (
        type: isPlayingDescriptionType,
        sentence: string | JSX.Element[] | undefined,
        currentPlayingItem: TrainingCenterBundleItemFullDescription,
        currentDecsription: FullDescriptionData
      ) => {
        if (sentence === undefined) {
          return [];
        }
        if (type !== currentPlayingItem.isPlaying) {
          return [<Fragment key={0}>{sentence}</Fragment>];
        }
        if (currentPlayingItem.isPlaying === false) {
          return [];
        }
        const marksFileKey:
          | "titleSpeechMarksFileUrl"
          | "subtitleSpeechMarksFileUrl"
          | "textSpeechMarksFileUrl" = (type + "SpeechMarksFileUrl") as
          | "titleSpeechMarksFileUrl"
          | "subtitleSpeechMarksFileUrl"
          | "textSpeechMarksFileUrl";
        const marksFileUrl = currentDecsription.speechFiles[marksFileKey];
        if (marksFileUrl === undefined) {
          return [<Fragment key={0}>{sentence}</Fragment>];
        }
        const currentSpeechMarks = speechMarks.current.get(marksFileUrl);
        if (currentSpeechMarks === undefined) {
          return [<Fragment key={0}>{sentence}</Fragment>];
        }

        let marked = false;
        return currentSpeechMarks.map(({ word, mark }, index) => {
          if (mark === null || marked) {
            return <span key={index}>{word + " "}</span>;
          }

          const nextMark = arrayAt(currentSpeechMarks, index + 1);
          const isAfterTime = mark.time >= currentAudioTime;
          const isBeforeEnd =
            nextMark === undefined ||
            (nextMark.mark !== null && nextMark.mark.time > currentAudioTime);

          if (isAfterTime && isBeforeEnd) {
            marked = true;
            return <mark key={index}>{word + " "}</mark>;
          }

          return <span key={index}>{word + " "}</span>;
        });
      },
      [currentAudioTime, speechMarks]
    );

    const hasSpeechMarks = React.useMemo(() => {
      return descriptions.some((description) => {
        return description.fullDescriptionData.some((descriptionData) => {
          if (descriptionData.data.speechFiles.titleSpeechMarksFileUrl !== undefined) {
            return true;
          }
          if (descriptionData.data.speechFiles.subtitleSpeechMarksFileUrl !== undefined) {
            return true;
          }
          if (descriptionData.data.speechFiles.textSpeechMarksFileUrl !== undefined) {
            return true;
          }
          return false;
        });
      });
    }, [descriptions]);

    if (props.fullDescriptions.length() === 0) {
      return <></>;
    }

    return (
      <TrainingCenterBundleItemFullDescriptionList
        fullDescriptions={descriptions}
        languagePreference={props.languagePreference}
        hasSpeechMarks={hasSpeechMarks}
        isPlaying={isPlaying}
        onPlayAudioClick={handlePlayPauseAudioClick}
        onAudioEnded={handleAudioPlayEnded}
        getText={getText}
        ref={currentAudioRef}
      />
    );
  }
);

export default TrainingCenterBundleItemFullDescriptionActivity;
